import { parseFillGapsStructuredBody, flattenFillGapParts, collectGapParts } from './gapParsing.js';

/** Nested under assignment.answers (jsonb). */
export const FILL_GAPS_AI_META = '__fill_gaps_ai_meta__';

/** Body marker: place an AI Check button; grades gaps since the previous marker (or start). */
export const FILL_GAPS_AI_CHECK_MARKER = '{ai:check}';
export const FILL_GAPS_AI_CHECK_MARKER_RE = /\{ai:check\}/gi;

const GAP_MARKER = (i) => `⟦GAP${i}⟧`;
const GAP_MARKER_RE = /⟦GAP\d+⟧/g;

export function readFillGapsAiMeta(answersObj) {
    return answersObj?.[FILL_GAPS_AI_META] || {};
}

export function extractFillGapsAiAnswersOnly(answersObj) {
    if (!answersObj || typeof answersObj !== 'object') return {};
    const { [FILL_GAPS_AI_META]: _m, ...rest } = answersObj;
    return rest;
}

/** Remove `{ai:check}` markers from body text (for gap parsing / display segments). */
export function stripFillGapsAiCheckMarkers(body) {
    return String(body || '').replace(FILL_GAPS_AI_CHECK_MARKER_RE, '');
}

/**
 * Split body into sections for scoped AI checks.
 * Each `{ai:check}` ends a section covering gaps since the previous marker (or start).
 * If none are present, one implicit section covers the whole exercise (button at end).
 * Trailing gaps after the last marker get an implicit end-of-section check.
 *
 * @returns {{
 *   pieces: Array<{ body: string, gapStart: number, gapEnd: number, gapIndices: number[] }>,
 *   scopes: Array<{ sectionIndex: number, pieceIndex: number, gapStart: number, gapEnd: number, gapIndices: number[], explicit: boolean }>
 * }}
 */
export function buildFillGapsAiCheckSections(body) {
    const raw = String(body || '');
    const piecesRaw = raw.split(FILL_GAPS_AI_CHECK_MARKER_RE);
    const hasExplicitMarkers = piecesRaw.length > 1;

    const pieces = [];
    let gapOffset = 0;
    for (let i = 0; i < piecesRaw.length; i += 1) {
        const pieceBody = piecesRaw[i];
        const gaps = collectGapParts(parseFillGapsStructuredBody(pieceBody));
        const gapStart = gapOffset;
        const gapEnd = gapOffset + gaps.length;
        const gapIndices = [];
        for (let g = gapStart; g < gapEnd; g += 1) gapIndices.push(g);
        pieces.push({ body: pieceBody, gapStart, gapEnd, gapIndices });
        gapOffset = gapEnd;
    }

    const scopes = [];
    for (let i = 0; i < pieces.length; i += 1) {
        const piece = pieces[i];
        const isLast = i === pieces.length - 1;
        const followedByMarker = i < pieces.length - 1;
        if (piece.gapIndices.length === 0) continue;
        if (followedByMarker) {
            scopes.push({
                sectionIndex: scopes.length,
                pieceIndex: i,
                gapStart: piece.gapStart,
                gapEnd: piece.gapEnd,
                gapIndices: piece.gapIndices,
                explicit: true,
            });
        } else if (!hasExplicitMarkers || piece.gapIndices.length > 0) {
            // No markers → one end check; or trailing gaps after last marker → end check
            scopes.push({
                sectionIndex: scopes.length,
                pieceIndex: i,
                gapStart: piece.gapStart,
                gapEnd: piece.gapEnd,
                gapIndices: piece.gapIndices,
                explicit: false,
            });
        }
    }

    // Degenerate: markers but somehow no scopes and there are gaps — one scope for all
    if (!scopes.length && gapOffset > 0) {
        const gapIndices = Array.from({ length: gapOffset }, (_, i) => i);
        scopes.push({
            sectionIndex: 0,
            pieceIndex: Math.max(0, pieces.length - 1),
            gapStart: 0,
            gapEnd: gapOffset,
            gapIndices,
            explicit: false,
        });
    }

    return { pieces, scopes, hasExplicitMarkers };
}

function formatSuggestedForGap(gapPart) {
    if (!gapPart?.answers?.length) return '';
    return gapPart.answers.join(' / ');
}

/** L1 / L2 / L3 / L4 from exercise.course (e.g. "L2", "L3 M1"). */
export function parseExerciseCourseLevel(course) {
    const m = String(course || '').trim().match(/^L(\d+)/i);
    return m ? Number(m[1]) : null;
}

/**
 * Feedback language rules by course level.
 * L1–L2 → Russian; L3 → ≤A2 English; L4 → ≤B1 English; other → natural English.
 */
export function fillGapsAiLanguageInstruction(course) {
    const level = parseExerciseCourseLevel(course);
    if (level === 1 || level === 2) {
        return 'LANGUAGE: Write ALL feedback and explanations in Russian (русский язык).';
    }
    if (level === 3) {
        return 'LANGUAGE: Write ALL feedback in simple English at CEFR A2 or below. Use short sentences and common words only.';
    }
    if (level === 4) {
        return 'LANGUAGE: Write ALL feedback in English at CEFR B1 or below. Keep it clear and not too advanced.';
    }
    return 'LANGUAGE: Write ALL feedback in clear natural English (no CEFR level adaptation required).';
}

/** True if text contains Cyrillic letters (typical Russian cue). */
export function textHasCyrillic(text) {
    return /[\u0400-\u04FF]/.test(String(text || ''));
}

/**
 * Best-effort cue from the line: prefer a Cyrillic (Russian) segment before Latin/English.
 * Falls back to text before the gap on the line.
 */
export function extractLikelyCue(lineBeforeGap) {
    const raw = String(lineBeforeGap || '').trim();
    if (!raw) return '';
    if (!textHasCyrillic(raw)) return raw;

    const split = raw.split(/(?<=[.!?…])\s+|(?:\s+[—–-]\s+)|(?:\s{2,})/);
    if (split.length >= 2) {
        const cyrillicParts = split.filter((p) => textHasCyrillic(p));
        if (cyrillicParts.length) return cyrillicParts.join(' ').trim();
    }

    const m = raw.match(/^([\s\S]*?[\u0400-\u04FF][\s\S]*?)(?:\s+[A-Za-z][\s\S]*)?$/);
    if (m && m[1] && textHasCyrillic(m[1])) return m[1].trim();
    return raw;
}

function skeletonReplaceGaps(text, currentGapIndex = null) {
    return String(text || '').replace(GAP_MARKER_RE, (m) => {
        const n = Number(m.slice(4, -1));
        if (currentGapIndex != null && n === currentGapIndex) return '[…]';
        return '___';
    });
}

/**
 * Per-gap context for AI: full line + before/after stem + likely cue (not only text before gap).
 */
export function buildFillGapsAiGapContexts(exerciseBody) {
    const parts = parseFillGapsStructuredBody(stripFillGapsAiCheckMarkers(exerciseBody || ''));
    const flat = flattenFillGapParts(parts);

    let skeleton = '';
    const gapMeta = [];
    let gapIdx = 0;

    for (const p of flat) {
        if (p.type === 'text') {
            skeleton += p.text;
        } else if (p.type === 'gap') {
            const marker = GAP_MARKER(gapIdx);
            gapMeta.push({
                gapIndex: gapIdx,
                marker,
                pos: skeleton.length,
                suggested: formatSuggestedForGap(p),
            });
            skeleton += marker;
            gapIdx += 1;
        }
    }

    return gapMeta.map((g) => {
        const lines = skeleton.split('\n');
        let lineIdx = -1;
        let lineRaw = '';
        for (let i = 0; i < lines.length; i += 1) {
            if (lines[i].includes(g.marker)) {
                lineIdx = i;
                lineRaw = lines[i];
                break;
            }
        }

        if (!lineRaw) {
            const start = Math.max(0, g.pos - 120);
            const end = Math.min(skeleton.length, g.pos + g.marker.length + 120);
            lineRaw = skeleton.slice(start, end);
        }

        const [beforeRaw = '', afterRaw = ''] = lineRaw.split(g.marker);
        let textBefore = beforeRaw.replace(GAP_MARKER_RE, '___').trim();
        const textAfter = afterRaw.replace(GAP_MARKER_RE, '___').trim();

        let prevLine = '';
        if (!textBefore && lineIdx > 0) {
            for (let j = lineIdx - 1; j >= 0; j -= 1) {
                const candidate = lines[j].replace(GAP_MARKER_RE, '___').trim();
                if (candidate) {
                    prevLine = candidate;
                    textBefore = candidate;
                    break;
                }
            }
        }

        let lineContext = skeletonReplaceGaps(lineRaw, g.gapIndex).trim();
        if (prevLine && (lineContext === '[…]' || !lineContext.replace(/\[…\]|___|\s/g, ''))) {
            lineContext = `${prevLine} […]`.trim();
        }

        const globalBefore = skeletonReplaceGaps(skeleton.slice(0, g.pos)).trim();
        const question = textBefore || globalBefore;
        const cue = extractLikelyCue(textBefore || question);

        return {
            gapIndex: g.gapIndex,
            question,
            lineContext,
            textBefore,
            textAfter,
            cue,
            suggested: g.suggested,
        };
    });
}

/**
 * @param {string} exerciseBody
 * @param {object} answersObj
 * @param {{ gapIndices?: number[] }} [opts] — if set, only those gap indexes
 */
export function collectFillGapsAiFeedbackItems(exerciseBody, answersObj, opts = {}) {
    const contexts = buildFillGapsAiGapContexts(exerciseBody);
    const gapAnswers = extractFillGapsAiAnswersOnly(answersObj);
    const allow = opts.gapIndices
        ? new Set(opts.gapIndices.map((n) => Number(n)))
        : null;
    return contexts
        .filter((ctx) => !allow || allow.has(ctx.gapIndex))
        .map((ctx) => ({
            gapIndex: ctx.gapIndex,
            question: ctx.question,
            lineContext: ctx.lineContext,
            textBefore: ctx.textBefore,
            textAfter: ctx.textAfter,
            cue: ctx.cue,
            studentAnswer: gapAnswers[ctx.gapIndex] ?? gapAnswers[String(ctx.gapIndex)] ?? '',
            suggested: ctx.suggested,
        }));
}

export function buildFillGapsAiSystemPrompt(course, { instruction } = {}) {
    const lang = fillGapsAiLanguageInstruction(course);
    const task = String(instruction || '').trim();
    return `You are an English language teacher checking a student's fill-in-the-gaps exercise.

STUDENT TASK / INSTRUCTION (authoritative — follow this when judging):
${task || '(No instruction provided — infer the task from the section text and gaps.)'}

For each gap you receive:
- Cue: a likely source prompt on the line (often Russian for translation tasks; may be empty)
- Full line: the whole exercise line with […] for this gap and ___ for other gaps
- Text before / after gap: stem around the blank
- Student answer: what the student typed
- Suggested answer: optional key (alternatives may be separated by " / "); may be empty
- Section text: the part of the exercise this check covers (may include context above the gaps)

PRIORITY:
1. Follow the STUDENT TASK / INSTRUCTION above. It defines what the gaps are for (translation, write questions, complete a dialogue, etc.).
2. If the instruction is a translation / meaning task (or Cue is a Russian prompt and instruction is empty/ambiguous), mark Incorrect when fluent English does not match the cue's meaning.
3. Do NOT assume every exercise is translation. If the instruction says students write questions, free answers, or creative fills, judge against that task — not against inventing a Russian cue.
4. Also check grammar, spelling, articles, tense, and collocation when relevant.
5. Compare against Suggested answer when present; accept valid paraphrases that still satisfy the instruction.
6. Empty answers are Incorrect.

For EACH gap, decide correct:true/false and write feedback as bullet points (lines starting with "- ") covering:
- Verdict (Correct / Incorrect)
- Task fit (does the answer do what the instruction asks?)
- Meaning / accuracy (when relevant)
- Grammar / spelling (if relevant)
- Better answer (when Incorrect or improvable): model fill or full phrase, then a short Because…

Be specific: quote the student's words. Keep feedback concise but useful.

${lang}

Return ONLY valid JSON — no markdown fences — in this shape:
{"0":{"correct":true,"feedback":"- Correct.\\n- …"},"1":{"correct":false,"feedback":"- Incorrect.\\n- …"}}

Keys are gap index strings for the gaps in THIS check batch, in the same order as listed ("0", "1", …).
"correct" must be a boolean.
"feedback" must be a multi-line string of bullet points (lines starting with "- ").`;
}

export function buildFillGapsAiUserPrompt(items, { instruction, sectionBody } = {}) {
    const task = String(instruction || '').trim();
    const section = String(sectionBody || '').trim();
    const header = [
        `Exercise instruction: ${task || '(none)'}`,
        section ? `Section text (this check):\n${section}` : null,
        '',
    ].filter((l) => l != null).join('\n');

    const gapsBlock = items.map((item, i) => (
        `--- Gap ${i} (exercise gap #${item.gapIndex}) ---
Cue: ${item.cue || '(none detected)'}
Full line: ${item.lineContext || item.question || '(no line context)'}
Text before gap: ${item.textBefore || item.question || '(none)'}
Text after gap: ${item.textAfter || '(none)'}
Student answer: ${item.studentAnswer || '(empty)'}
Suggested answer: ${item.suggested || '(no answer key)'}`
    )).join('\n\n');

    return `${header}\n${gapsBlock}`;
}

/**
 * Remap AI batch keys ("0","1",…) onto real gap indexes from items[].gapIndex.
 */
export function remapFillGapsAiBatchResults(items, batchResult) {
    const feedback = {};
    const isCorrect = {};
    const srcFb = batchResult?.feedback || {};
    const srcOk = batchResult?.isCorrect || {};
    (items || []).forEach((item, i) => {
        const batchKey = String(i);
        const gapKey = String(item.gapIndex);
        const fb = srcFb[batchKey] ?? srcFb[i] ?? '';
        if (fb) feedback[gapKey] = fb;
        const ok = srcOk[batchKey] ?? srcOk[i];
        if (ok === true || ok === false) {
            isCorrect[gapKey] = ok;
            isCorrect[item.gapIndex] = ok;
        }
    });
    return { feedback, isCorrect };
}

/**
 * @returns {{ feedback: Record<string,string>, isCorrect: Record<string,boolean> }}
 */
export function parseFillGapsAiFeedbackResponse(content, expectedCount) {
    let parsed;
    try {
        const trimmed = (content || '').trim();
        const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
        parsed = JSON.parse(jsonMatch ? jsonMatch[0] : trimmed);
    } catch {
        if (expectedCount === 1) {
            return {
                feedback: { '0': (content || '').trim() },
                isCorrect: {},
            };
        }
        throw new Error('AI returned invalid JSON. Please try again.');
    }

    const feedback = {};
    const isCorrect = {};
    for (let i = 0; i < expectedCount; i += 1) {
        const key = String(i);
        const raw = parsed[key] ?? parsed[i];
        if (raw && typeof raw === 'object') {
            feedback[key] = (raw.feedback ?? raw.comment ?? '').toString().trim();
            if (raw.correct === true || raw.correct === false) {
                isCorrect[key] = raw.correct;
                isCorrect[i] = raw.correct;
            }
        } else {
            feedback[key] = (raw ?? '').toString().trim();
        }
    }
    return { feedback, isCorrect };
}

export function buildFillGapsAiAnswersPayload(gapAnswers, metaPatch, existingAnswersObj) {
    const prev = readFillGapsAiMeta(existingAnswersObj);
    return {
        ...gapAnswers,
        [FILL_GAPS_AI_META]: {
            ...prev,
            ...metaPatch,
        },
    };
}

/** True when every AI-check section has been completed. */
export function areAllFillGapsAiSectionsChecked(scopes, sectionChecks, legacyAiChecked) {
    if (!scopes?.length) return !!legacyAiChecked;
    if (scopes.length === 1 && legacyAiChecked && !sectionChecks) return true;
    return scopes.every((s) => !!(sectionChecks && sectionChecks[String(s.sectionIndex)]));
}
